import { useState, useEffect } from "react";
import {
  RotateCcw, Search, X, RefreshCw, Check, Plus,
  Package, AlertCircle, Eye, DollarSign,
} from "lucide-react";
import { useAdmin } from "../admin-context";

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  requested:  { cls: "bg-yellow-100 text-yellow-800",  label: "Requested"  },
  approved:   { cls: "bg-blue-100 text-blue-800",      label: "Approved"   },
  rejected:   { cls: "bg-red-100 text-red-800",        label: "Rejected"   },
  refunded:   { cls: "bg-green-100 text-green-800",    label: "Refunded"   },
  completed:  { cls: "bg-gray-100 text-gray-600",      label: "Completed"  },
};

const RETURN_REASONS = [
  "Wrong item received",
  "Item damaged / defective",
  "Item not as described",
  "Changed my mind",
  "Duplicate order",
  "Other",
];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtCurrency = (v) =>
  v != null ? `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";

// ── Shared UI ─────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || { cls: "bg-gray-100 text-gray-500", label: status };
  return <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
};

const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white h-full max-w-md w-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

const inp = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent";
const sel = inp + " bg-white";

// ── Create Return Drawer ──────────────────────────────────────────────────────
const CreateDrawer = ({ open, onClose, orders, onSave }) => {
  const [form, setForm] = useState({
    order_id: "", customer_name: "", customer_email: "",
    item_name: "", reason: RETURN_REASONS[0], refund_amount: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleOrderChange = (e) => {
    const id  = e.target.value;
    const ord = (orders || []).find(o => String(o.id) === id);
    setForm(p => ({
      ...p,
      order_id:       id,
      customer_name:  ord?.customer_name  || ord?.name  || "",
      customer_email: ord?.customer_email || ord?.email || "",
      refund_amount:  ord?.total_amount   || ord?.total || "",
    }));
  };

  const handleSave = async () => {
    if (!form.order_id)      return;
    if (!form.customer_name) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
    onClose();
  };

  return (
    <Drawer open={open} onClose={onClose} title="New Return Request">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Order<span className="text-red-500 ml-0.5">*</span></label>
          <select className={sel} value={form.order_id} onChange={handleOrderChange}>
            <option value="">Select order…</option>
            {(orders || []).map(o => (
              <option key={o.id} value={o.id}>#{o.id} — {o.customer_name || o.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Customer Name</label>
          <input className={inp} value={form.customer_name} onChange={f("customer_name")} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Customer Email</label>
          <input className={inp} value={form.customer_email} onChange={f("customer_email")} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Item / Description</label>
          <input className={inp} value={form.item_name} onChange={f("item_name")} placeholder="What is being returned?" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Reason</label>
          <select className={sel} value={form.reason} onChange={f("reason")}>
            {RETURN_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Refund Amount (₹)</label>
          <input type="number" min={0} className={inp} value={form.refund_amount} onChange={f("refund_amount")} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Internal Notes</label>
          <textarea rows={3} className={inp} value={form.notes} onChange={f("notes")} placeholder="Optional notes for the team…" />
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.order_id}
            className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            Create Request
          </button>
        </div>
      </div>
    </Drawer>
  );
};

// ── Detail Drawer ─────────────────────────────────────────────────────────────
const DetailDrawer = ({ open, onClose, ret, onUpdate }) => {
  const [status, setStatus] = useState(ret?.status || "requested");
  const [notes,  setNotes]  = useState(ret?.admin_notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ret) { setStatus(ret.status); setNotes(ret.admin_notes || ""); }
  }, [ret]);

  if (!ret) return null;

  const handleSave = async () => {
    setSaving(true);
    await onUpdate(ret.id, { status, admin_notes: notes });
    setSaving(false);
    onClose();
  };

  return (
    <Drawer open={open} onClose={onClose} title={`Return #${ret.id}`}>
      <div className="space-y-5">
        {/* Summary */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Order</span><span className="font-semibold">#{ret.order_id}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Customer</span><span className="font-semibold">{ret.customer_name}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Item</span><span className="font-semibold">{ret.item_name || "—"}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Reason</span><span className="font-semibold">{ret.reason}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Requested</span><span className="font-semibold">{fmtDate(ret.created_at)}</span></div>
          <div className="flex justify-between">
            <span className="text-gray-500">Refund Amount</span>
            <span className="font-bold text-green-700">{fmtCurrency(ret.refund_amount)}</span>
          </div>
        </div>

        {/* Customer notes */}
        {ret.notes && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
            <p className="font-semibold text-xs uppercase tracking-wider mb-1 text-amber-600">Customer Note</p>
            {ret.notes}
          </div>
        )}

        {/* Update status */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Update Status</label>
          <select className={sel} value={status} onChange={e => setStatus(e.target.value)}>
            {Object.entries(STATUS_STYLES).map(([v, { label }]) => (
              <option key={v} value={v}>{label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Admin Notes</label>
          <textarea rows={3} className={inp} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Internal notes (not visible to customer)…" />
        </div>

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            Save
          </button>
        </div>
      </div>
    </Drawer>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const ReturnsPage = () => {
  const { returns, loadReturns, createReturn, updateReturn, orders, showToast } = useAdmin();
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen,   setCreateOpen]   = useState(false);
  const [detailItem,   setDetailItem]   = useState(null);

  useEffect(() => { loadReturns && loadReturns(); }, []);

  const list = (returns || []).filter(r => {
    const matchStatus = statusFilter === "all" || r.status === statusFilter;
    const q = search.toLowerCase();
    const matchSearch = !q || String(r.id).includes(q)
      || (r.customer_name || "").toLowerCase().includes(q)
      || (r.customer_email || "").toLowerCase().includes(q)
      || String(r.order_id).includes(q);
    return matchStatus && matchSearch;
  });

  const handleCreate = async (data) => {
    try {
      await createReturn({ ...data, status: "requested" });
      showToast("Return request created");
    } catch {
      showToast("Failed to create return", "error");
    }
  };

  const handleUpdate = async (id, patch) => {
    try {
      await updateReturn(id, patch);
      showToast("Return updated");
    } catch {
      showToast("Failed to update", "error");
    }
  };

  const stats = Object.entries(STATUS_STYLES).map(([k, { label }]) => ({
    key: k, label,
    count: (returns || []).filter(r => r.status === k).length,
  }));

  const totalRefund = (returns || [])
    .filter(r => r.status === "refunded")
    .reduce((s, r) => s + Number(r.refund_amount || 0), 0);

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Returns & Refunds</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage customer return requests and refund approvals</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => loadReturns && loadReturns()} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition">
            <RefreshCw size={16} className="text-gray-500" />
          </button>
          <button onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold text-sm transition">
            <Plus size={16} /> New Request
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map(({ key, label, count }) => (
          <button key={key}
            onClick={() => setStatusFilter(statusFilter === key ? "all" : key)}
            className={`rounded-2xl p-3 text-left border-2 transition ${statusFilter === key ? "border-orange-400 bg-orange-50" : "border-transparent bg-white hover:border-gray-200"}`}>
            <p className="text-xl font-extrabold text-gray-900">{count}</p>
            <p className="text-xs font-semibold text-gray-500 mt-0.5">{label}</p>
          </button>
        ))}
        <div className="rounded-2xl p-3 bg-green-50 border-2 border-transparent col-span-2 sm:col-span-1">
          <p className="text-xl font-extrabold text-green-700">₹{totalRefund.toLocaleString("en-IN")}</p>
          <p className="text-xs font-semibold text-green-600 mt-0.5">Total Refunded</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          placeholder="Search by order, customer…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              {["Return #","Order","Customer","Item","Reason","Refund","Status","Date",""].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {list.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">
                <RotateCcw size={28} className="mx-auto mb-2 opacity-30" />
                No return requests found
              </td></tr>
            )}
            {list.map(r => (
              <tr key={r.id} className="hover:bg-gray-50 transition">
                <td className="px-4 py-3 font-mono text-xs text-gray-500">#{r.id}</td>
                <td className="px-4 py-3 font-semibold text-gray-900">#{r.order_id}</td>
                <td className="px-4 py-3">
                  <p className="font-semibold text-gray-900">{r.customer_name}</p>
                  <p className="text-xs text-gray-400">{r.customer_email}</p>
                </td>
                <td className="px-4 py-3 text-gray-600 max-w-[140px] truncate">{r.item_name || "—"}</td>
                <td className="px-4 py-3 text-gray-500 max-w-[120px] truncate">{r.reason}</td>
                <td className="px-4 py-3 font-semibold text-gray-900">{fmtCurrency(r.refund_amount)}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3">
                  <button onClick={() => setDetailItem(r)}
                    className="p-1.5 hover:bg-orange-50 rounded-lg transition">
                    <Eye size={14} className="text-orange-500" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drawers */}
      <CreateDrawer open={createOpen} onClose={() => setCreateOpen(false)} orders={orders} onSave={handleCreate} />
      <DetailDrawer open={!!detailItem} onClose={() => setDetailItem(null)} ret={detailItem} onUpdate={handleUpdate} />
    </div>
  );
};

export default ReturnsPage;
